import { isEqual } from 'lodash'
import { createJSONStorage, persist } from 'zustand/middleware'
import { createWithEqualityFn } from 'zustand/traditional'

import { useConversationStore } from './useConversationStore'
import { getHostRuntime } from '../engine/host-runtime'
import { getSessionContext } from '../engine/session-context'
import type { AiChatbotConfig } from '../engine/types'
import { generateUuid7 } from '../engine/uuid7'
import { buildConfig } from '../tools'

interface FabPosition {
  x: number
  y: number
}

interface OpenOpts {
  promptHint?: string
  displayMessage?: string
}

type AiChatBotMode = 'default' | 'headless'

interface AiChatBotState {
  isOpen: boolean
  isAnimatingOut: boolean
  activeId: string | undefined
  activeConfig: AiChatbotConfig | undefined
  fabPosition: FabPosition | null
  mode: AiChatBotMode
  featureFlags: Set<string>
  open: (opts?: OpenOpts) => void
  close: () => void
  toggle: (opts?: OpenOpts) => void
  animationDidEnd: () => void
  setActiveId: (id: string) => void
  setFabPosition: (pos: FabPosition) => void
  setMode: (mode: AiChatBotMode) => void
  setFeatureFlags: (flags: Set<string>) => void
}

const STORAGE_KEY = 'neodxp-ai-ui'

export const useAiChatBotStore = createWithEqualityFn<AiChatBotState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      isAnimatingOut: false,
      activeId: undefined,
      activeConfig: undefined,
      fabPosition: null,
      mode: 'default',
      featureFlags: new Set<string>(),

      open: ({ promptHint, displayMessage } = {}) => {
        if (get().mode === 'headless') {
          // The host presents the panel in its shell (e.g. a drawer) and wires the
          // shell's dismiss control back through get().close() so the conversation
          // stops and isOpen resets on the X button too. A false return means the
          // shell is occupied — abort the open.
          const presented = getHostRuntime().bindings.presentPanel?.(() =>
            get().close(),
          )
          if (presented === false) return
        }

        set({ isOpen: true, isAnimatingOut: false })

        void getSessionContext().then((ctx) => {
          const featureFlags = get().featureFlags
          const config = buildConfig(
            promptHint,
            displayMessage,
            undefined,
            featureFlags,
          )
          // Bail if the user closed the panel before the async part resolved
          if (!get().isOpen) return
          const conversationStore = useConversationStore.getState()

          conversationStore.setUserId(ctx.currentUserId)

          const id = generateUuid7()
          set({ activeId: id, activeConfig: config })
          conversationStore.init(id, config)
        })
      },

      close: () => {
        useConversationStore.getState().stop()
        set({ isOpen: false, isAnimatingOut: true })
        if (get().mode === 'headless') {
          getHostRuntime().bindings.dismissPanel?.()
        }
      },

      toggle: (opts) => {
        if (get().isOpen) get().close()
        else get().open(opts)
      },

      animationDidEnd: () => set({ isAnimatingOut: false }),
      setActiveId: (id) => set({ activeId: id }),
      setFabPosition: (pos) => set({ fabPosition: pos }),
      setMode: (mode) => set({ mode }),
      setFeatureFlags: (flags) => {
        set({ featureFlags: flags })
        // Host runtime (capabilities) is set by useAiChatBotEnabled via setHostRuntime.
        // Session context reads capabilities from getHostRuntime().bindings.capabilities.
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ fabPosition: s.fabPosition }),
    },
  ),
  // Enables grouped destructure selectors (e.g. `const { a, b } = useStore(s => ({ a: s.a, b: s.b }))`)
  // without per-call useShallow. Removing this would cause those selectors to re-render on every store update.
  isEqual,
)
